// IndexedDB Helper for Habit Tracker
// All habit data is stored locally on device - NO cloud storage for logs

const DB_NAME = 'BurningTeamHabits';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('habits')) {
        const habitStore = db.createObjectStore('habits', { keyPath: 'id' });
        habitStore.createIndex('status', 'status', { unique: false });
      }
      if (!db.objectStoreNames.contains('habitLogs')) {
        const logStore = db.createObjectStore('habitLogs', { keyPath: 'id' });
        logStore.createIndex('habitId', 'habitId', { unique: false });
        logStore.createIndex('date', 'date', { unique: false });
        logStore.createIndex('habitId_date', ['habitId', 'date'], { unique: false });
      }
    };
  });
}

// --- HABITS CRUD ---

export async function getAllHabits() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('habits', 'readonly');
    const store = tx.objectStore('habits');
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getActiveHabits() {
  const all = await getAllHabits();
  return all.filter(h => h.status !== 'archived' && h.status !== 'paused');
}

export async function getHabitById(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('habits', 'readonly');
    const request = tx.objectStore('habits').get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveHabit(habit) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('habits', 'readwrite');
    tx.objectStore('habits').put({ ...habit, updatedAt: new Date().toISOString() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteHabit(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(['habits', 'habitLogs'], 'readwrite');
    tx.objectStore('habits').delete(id);
    // Also delete all logs for this habit
    const logStore = tx.objectStore('habitLogs');
    const index = logStore.index('habitId');
    const request = index.openCursor(IDBKeyRange.only(id));
    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// --- HABIT LOGS CRUD ---

export async function getLogsForHabit(habitId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('habitLogs', 'readonly');
    const index = tx.objectStore('habitLogs').index('habitId');
    const request = index.getAll(IDBKeyRange.only(habitId));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getLogForDate(habitId, date) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('habitLogs', 'readonly');
    const index = tx.objectStore('habitLogs').index('habitId_date');
    const request = index.get([habitId, date]);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function getLogsForDateRange(habitId, startDate, endDate) {
  const logs = await getLogsForHabit(habitId);
  return logs.filter(l => l.date >= startDate && l.date <= endDate);
}

export async function getAllLogsForDate(date) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('habitLogs', 'readonly');
    const index = tx.objectStore('habitLogs').index('date');
    const request = index.getAll(IDBKeyRange.only(date));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveLog(log) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('habitLogs', 'readwrite');
    tx.objectStore('habitLogs').put(log);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function toggleHabitDone(habitId, date) {
  const existing = await getLogForDate(habitId, date);
  if (existing) {
    existing.done = !existing.done;
    existing.updatedAt = new Date().toISOString();
    await saveLog(existing);
    return existing;
  } else {
    const newLog = {
      id: `${habitId}_${date}`,
      habitId,
      date,
      done: true,
      value: null,
      rating: null,
      note: '',
      createdAt: new Date().toISOString()
    };
    await saveLog(newLog);
    return newLog;
  }
}

export async function savePerformanceLog(habitId, date, value, rating) {
  const existing = await getLogForDate(habitId, date);
  const log = existing || {
    id: `${habitId}_${date}`,
    habitId,
    date,
    note: '',
    createdAt: new Date().toISOString()
  };
  log.done = true;
  log.value = value;
  log.rating = rating || null;
  log.updatedAt = new Date().toISOString();
  await saveLog(log);
  return log;
}
